const AsyncStorage = require('@react-native-async-storage/async-storage').default;

class BusinessProfessionalIntegration {
  constructor() {
    this.isInitialized = false;
    this.professionals = [];
    this.communications = [];
    this.contactHistory = [];
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('🔄 Initializing BusinessProfessionalIntegration...');
      
      // Load cached data
      await this.loadCachedData();
      
      // Generate mock professionals if none exist
      if (this.professionals.length === 0) {
        this.professionals = this.generateMockProfessionals();
        await this.saveProfessionals();
      }
      
      this.isInitialized = true;
      console.log('✅ BusinessProfessionalIntegration initialized successfully');
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error initializing BusinessProfessionalIntegration:', error);
      this.isInitialized = true; // Still mark as initialized to prevent blocking
      return { success: false, error: error.message };
    }
  }

  // Load cached data from storage
  async loadCachedData() {
    try {
      const [professionalsData, communicationsData, historyData] = await Promise.all([
        AsyncStorage.getItem('business_professionals'),
        AsyncStorage.getItem('business_communications'),
        AsyncStorage.getItem('business_contact_history'),
      ]);

      if (professionalsData) {
        this.professionals = JSON.parse(professionalsData);
      }
      if (communicationsData) {
        this.communications = JSON.parse(communicationsData);
      }
      if (historyData) {
        this.contactHistory = JSON.parse(historyData);
      }

      console.log('📱 Loaded cached business-professional data');
    } catch (error) {
      console.error('Error loading cached data:', error);
    }
  }

  // Save professionals to storage
  async saveProfessionals() {
    try {
      await AsyncStorage.setItem('business_professionals', JSON.stringify(this.professionals));
    } catch (error) {
      console.error('Error saving professionals:', error);
    }
  }

  // Save communications to storage
  async saveCommunications() {
    try {
      await AsyncStorage.setItem('business_communications', JSON.stringify(this.communications));
    } catch (error) {
      console.error('Error saving communications:', error);
    }
  }

  // Save contact history to storage
  async saveContactHistory() {
    try {
      await AsyncStorage.setItem('business_contact_history', JSON.stringify(this.contactHistory));
    } catch (error) {
      console.error('Error saving contact history:', error);
    }
  }

  // Generate mock professional data
  generateMockProfessionals() {
    return [
      {
        id: 'prof_001',
        name: 'Sarah Johnson',
        title: 'Senior Full Stack Developer',
        company: 'Tech Solutions Ltd',
        location: 'London, UK',
        skills: ['React', 'Node.js', 'Python', 'AWS', 'MongoDB'],
        experience: '5+ years',
        rating: 4.8,
        availability: 'Available',
        verified: true,
        premium: true,
        email: 'sarah.johnson@email.com',
        phone: '+44 7700 900123',
        portfolio: 'https://sarahjohnson.dev',
        bio: 'Experienced full-stack developer with expertise in modern web technologies and cloud platforms.',
        certifications: ['AWS Certified Developer', 'React Professional'],
        responseRate: 95,
        lastActive: '2 hours ago',
        contactPreferences: ['email', 'phone', 'video_call'],
        hourlyRate: '£45-65',
        projects: 47,
        successRate: 98,
      },
      {
        id: 'prof_002',
        name: 'Michael Chen',
        title: 'DevOps Engineer',
        company: 'Cloud Innovations',
        location: 'Manchester, UK',
        skills: ['Docker', 'Kubernetes', 'Jenkins', 'Terraform', 'Azure'],
        experience: '7+ years',
        rating: 4.9,
        availability: 'Available',
        verified: true,
        premium: false,
        email: 'michael.chen@email.com',
        phone: '+44 7700 900124',
        portfolio: 'https://michaelchen.tech',
        bio: 'DevOps specialist focused on automation, scalability, and cloud infrastructure optimization.',
        certifications: ['Azure DevOps Expert', 'Kubernetes Administrator'],
        responseRate: 92,
        lastActive: '1 hour ago',
        contactPreferences: ['email', 'video_call'],
        hourlyRate: '£55-75',
        projects: 63,
        successRate: 96,
      },
      {
        id: 'prof_003',
        name: 'Emma Williams',
        title: 'UX/UI Designer',
        company: 'Design Studio Pro',
        location: 'Birmingham, UK',
        skills: ['Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'User Research'],
        experience: '4+ years',
        rating: 4.7,
        availability: 'Busy',
        verified: true,
        premium: true,
        email: 'emma.williams@email.com',
        phone: '+44 7700 900125',
        portfolio: 'https://emmawilliams.design',
        bio: 'Creative UX/UI designer passionate about creating intuitive and beautiful user experiences.',
        certifications: ['Google UX Design Certificate', 'Adobe Certified Expert'],
        responseRate: 88,
        lastActive: '30 minutes ago',
        contactPreferences: ['email', 'messaging'],
        hourlyRate: '£40-60',
        projects: 34,
        successRate: 94,
      },
      {
        id: 'prof_004',
        name: 'James Rodriguez',
        title: 'Data Scientist',
        company: 'Analytics Corp',
        location: 'Edinburgh, UK',
        skills: ['Python', 'R', 'Machine Learning', 'TensorFlow', 'SQL'],
        experience: '6+ years',
        rating: 4.9,
        availability: 'Available',
        verified: true,
        premium: false,
        email: 'james.rodriguez@email.com',
        phone: '+44 7700 900126',
        portfolio: 'https://jamesrodriguez.ai',
        bio: 'Data scientist with expertise in machine learning, predictive analytics, and business intelligence.',
        certifications: ['Google Cloud ML Engineer', 'Microsoft Azure AI Engineer'],
        responseRate: 97,
        lastActive: '15 minutes ago',
        contactPreferences: ['email', 'phone', 'video_call'],
        hourlyRate: '£50-70',
        projects: 28,
        successRate: 99,
      },
      {
        id: 'prof_005',
        name: 'Lisa Thompson',
        title: 'Mobile App Developer',
        company: 'Mobile Solutions UK',
        location: 'Bristol, UK',
        skills: ['React Native', 'Flutter', 'iOS', 'Android', 'Firebase'],
        experience: '5+ years',
        rating: 4.6,
        availability: 'Available',
        verified: false,
        premium: true,
        email: 'lisa.thompson@email.com',
        phone: '+44 7700 900127',
        portfolio: 'https://lisathompson.mobile',
        bio: 'Mobile app developer specializing in cross-platform solutions and native iOS/Android development.',
        certifications: ['Google Associate Android Developer', 'Apple iOS Developer'],
        responseRate: 90,
        lastActive: '1 day ago',
        contactPreferences: ['email', 'messaging'],
        hourlyRate: '£42-62',
        projects: 41,
        successRate: 93,
      },
    ];
  }

  // Search professionals with filters
  async searchProfessionals(filters = {}) {
    try {
      console.log('🔍 Searching professionals with filters:', filters);
      
      let results = [...this.professionals];
      
      // Apply filters
      if (filters.skills && filters.skills.length > 0) {
        results = results.filter(prof => 
          filters.skills.some(skill => 
            prof.skills.some(profSkill => 
              profSkill.toLowerCase().includes(skill.toLowerCase())
            )
          )
        );
      }
      
      if (filters.location) {
        results = results.filter(prof => 
          prof.location.toLowerCase().includes(filters.location.toLowerCase())
        );
      }
      
      if (filters.availability) {
        results = results.filter(prof => 
          prof.availability.toLowerCase() === filters.availability.toLowerCase()
        );
      }
      
      if (filters.minRating) {
        results = results.filter(prof => prof.rating >= filters.minRating);
      }
      
      if (filters.verified !== undefined) {
        results = results.filter(prof => prof.verified === filters.verified);
      }
      
      if (filters.premium !== undefined) {
        results = results.filter(prof => prof.premium === filters.premium);
      }
      
      // Sort results
      if (filters.sortBy) {
        results.sort((a, b) => {
          switch (filters.sortBy) {
            case 'rating':
              return b.rating - a.rating;
            case 'experience':
              return parseInt(b.experience) - parseInt(a.experience);
            case 'responseRate':
              return b.responseRate - a.responseRate;
            case 'projects':
              return b.projects - a.projects;
            default:
              return 0;
          }
        });
      }
      
      console.log(`✅ Found ${results.length} professionals`);
      return { success: true, professionals: results };
      
    } catch (error) {
      console.error('❌ Error searching professionals:', error);
      return { success: false, error: error.message, professionals: [] };
    }
  }

  // Get professional by ID
  async getProfessional(professionalId) {
    try {
      const professional = this.professionals.find(prof => prof.id === professionalId);
      
      if (professional) {
        return { success: true, professional };
      } else {
        return { success: false, error: 'Professional not found' };
      }
    } catch (error) {
      console.error('❌ Error getting professional:', error);
      return { success: false, error: error.message };
    }
  }

  // Contact a professional
  async contactProfessional(professionalId, contactData) {
    try {
      console.log('📞 Contacting professional:', professionalId, contactData);
      
      const professional = this.professionals.find(prof => prof.id === professionalId);
      if (!professional) {
        return { success: false, error: 'Professional not found' };
      }
      
      // Create communication record
      const communication = {
        id: `comm_${Date.now()}`,
        professionalId,
        professionalName: professional.name,
        type: contactData.type,
        subject: contactData.subject,
        message: contactData.message,
        timestamp: new Date().toISOString(),
        status: 'sent',
        businessData: contactData.businessData || {},
      };
      
      this.communications.push(communication);
      await this.saveCommunications();
      
      // Add to contact history
      const historyEntry = {
        id: `history_${Date.now()}`,
        professionalId,
        professionalName: professional.name,
        action: `Sent ${contactData.type}`,
        timestamp: new Date().toISOString(),
        details: contactData.subject,
      };
      
      this.contactHistory.push(historyEntry);
      await this.saveContactHistory();
      
      console.log('✅ Professional contacted successfully');
      return { 
        success: true, 
        communication,
        message: `${contactData.type} sent to ${professional.name} successfully!`
      };
      
    } catch (error) {
      console.error('❌ Error contacting professional:', error);
      return { success: false, error: error.message };
    }
  }

  // Get communication history
  async getCommunicationHistory(professionalId = null) {
    try {
      let history = [...this.communications];
      
      if (professionalId) {
        history = history.filter(comm => comm.professionalId === professionalId);
      }
      
      // Sort by timestamp (newest first)
      history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      return { success: true, communications: history };
    } catch (error) {
      console.error('❌ Error getting communication history:', error);
      return { success: false, error: error.message, communications: [] };
    }
  }

  // Get contact history
  async getContactHistory() {
    try {
      const history = [...this.contactHistory];
      history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      return { success: true, history };
    } catch (error) {
      console.error('❌ Error getting contact history:', error);
      return { success: false, error: error.message, history: [] };
    }
  }

  // Get statistics
  async getStatistics() {
    try {
      const stats = {
        totalProfessionals: this.professionals.length,
        availableProfessionals: this.professionals.filter(p => p.availability === 'Available').length,
        verifiedProfessionals: this.professionals.filter(p => p.verified).length,
        premiumProfessionals: this.professionals.filter(p => p.premium).length,
        totalCommunications: this.communications.length,
        recentCommunications: this.communications.filter(c => {
          const commDate = new Date(c.timestamp);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return commDate > weekAgo;
        }).length,
        averageRating: this.professionals.reduce((sum, p) => sum + p.rating, 0) / this.professionals.length,
        averageResponseRate: this.professionals.reduce((sum, p) => sum + p.responseRate, 0) / this.professionals.length,
      };
      
      return { success: true, stats };
    } catch (error) {
      console.error('❌ Error getting statistics:', error);
      return { success: false, error: error.message, stats: {} };
    }
  }

  // Schedule interview
  async scheduleInterview(professionalId, interviewData) {
    try {
      const contactData = {
        type: 'interview_request',
        subject: `Interview Request - ${interviewData.position}`,
        message: `We would like to schedule an interview for the ${interviewData.position} position. 
                 Proposed time: ${interviewData.dateTime}
                 Duration: ${interviewData.duration || '1 hour'}
                 Type: ${interviewData.type || 'Video call'}`,
        businessData: interviewData,
      };
      
      return await this.contactProfessional(professionalId, contactData);
    } catch (error) {
      console.error('❌ Error scheduling interview:', error);
      return { success: false, error: error.message };
    }
  }

  // Send job offer
  async sendJobOffer(professionalId, offerData) {
    try {
      const contactData = {
        type: 'job_offer',
        subject: `Job Offer - ${offerData.position}`,
        message: `We are pleased to offer you the position of ${offerData.position}.
                 Salary: ${offerData.salary}
                 Start Date: ${offerData.startDate}
                 Location: ${offerData.location}`,
        businessData: offerData,
      };
      
      return await this.contactProfessional(professionalId, contactData);
    } catch (error) {
      console.error('❌ Error sending job offer:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create singleton instance
const businessProfessionalIntegration = new BusinessProfessionalIntegration();

module.exports = businessProfessionalIntegration;

